import { motion } from "framer-motion";
import {
  Code2,
  Bot,
  Cloud,
  Cpu,
  Printer,
  Compass,
  Check,
} from "lucide-react";

const SERVICES = [
  {
    icon: Code2,
    title: "Web & App Development",
    desc: "Full-stack web applications and mobile experiences engineered for scale, speed, and craft. React, Next.js, Node.js — from system architecture down to pixel-perfect UI, deployed production-ready from day one.",
    features: ["React / Next.js / Angular", "Progressive Web Apps", "API Design & Integration", "Performance Optimization"],
    tag: "001 / Engineering",
    accent: "none",
  },
  {
    icon: Bot,
    title: "AI Product Development",
    desc: "LLMs, RAG pipelines, autonomous agents, and AI-native products that work in production — not just in demos. We integrate intelligence into your product where it actually matters.",
    features: ["Large Language Models", "Natural Language Processing", "Computer Vision", "Predictive Analytics"],
    tag: "002 / Intelligence",
    accent: "purple",
  },
  {
    icon: Cloud,
    title: "SaaS Platforms",
    desc: "Multi-tenant architectures, billing systems, auth, and cloud-native backends built to scale from day one. Not retrofitted — designed for growth at every layer.",
    features: ["Multi-tenant Architecture", "Subscription Management", "Scalable Infrastructure", "Analytics & Reporting"],
    tag: "003 / SaaS",
    accent: "cyan",
  },
  {
    icon: Cpu,
    title: "Hardware & IoT",
    desc: "Embedded systems, firmware, and connected devices using Arduino, Raspberry Pi, and ESP32. From sensor fusion to real-time edge processing — we build hardware that thinks.",
    features: ["Sensor Fusion & Edge AI", "MQTT / Real-time Data", "Custom PCB & Firmware", "IoT Cloud Integration"],
    tag: "004 / Hardware",
    accent: "purple",
  },
  {
    icon: Printer,
    title: "3D Printing & Prototyping",
    desc: "Custom enclosures, mechanical parts, and rapid prototypes using FDM and SLA printing. From Fusion 360 CAD to final manufactured part — we take your hardware from concept to physical reality.",
    features: ["Rapid Prototyping", "Custom Enclosure Design", "FDM & SLA Printing", "CAD / Fusion 360"],
    tag: "005 / Fabrication",
    accent: "cyan",
  },
  {
    icon: Compass,
    title: "Strategic Consulting",
    desc: "Fractional CTO support, architecture reviews, and technical roadmaps for founders who need an experienced voice before committing engineering resources.",
    features: ["Product Strategy & Roadmap", "Architecture Reviews", "Go-to-Market Planning", "Technical Due Diligence"],
    tag: "006 / Advisory",
    accent: "none",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      className="relative py-24 md:py-32"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Section heading */}
        <div className="max-w-3xl mb-14 md:mb-20">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">
            / 01 — What we build
          </div>
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={{
              visible: { transition: { staggerChildren: 0.1 } },
            }}
            className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            data-testid="services-heading"
          >
            <motion.span 
              variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } } }} 
              className="block"
            >
              One studio.
            </motion.span>
            <motion.span 
              variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } } }} 
              className="block text-zinc-500"
            >
              Every layer.
            </motion.span>
          </motion.h2>
          <p className="mt-6 text-zinc-400 max-w-xl text-lg font-light">
            From web app to connected hardware with a printed enclosure,
            every layer of modern product development runs through one studio.
            Every handoff eliminated by design.
          </p>
        </div>

        {/* Bento grid */}
        <div className="bento-grid">
          {SERVICES.map((s, idx) => {
            const Icon = s.icon;
            let colSpanClass = "col-span-12";
            if (idx === 0) colSpanClass = "col-span-12 md:col-span-7";
            else if (idx === 1) colSpanClass = "col-span-12 md:col-span-5";
            else if (idx === 2) colSpanClass = "col-span-12 md:col-span-5";
            else if (idx === 3) colSpanClass = "col-span-12 md:col-span-7";
            else if (idx === 4) colSpanClass = "col-span-12 md:col-span-7";
            else if (idx === 5) colSpanClass = "col-span-12 md:col-span-5";

            return (
              <motion.article
                key={s.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, delay: idx * 0.07 }}
                className={`group glass-card bento-item p-8 md:p-10 flex flex-col justify-between ${colSpanClass}`}
                data-testid={`service-card-${idx}`}
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const y = e.clientY - rect.top;
                  e.currentTarget.style.setProperty('--x', `${x}px`);
                  e.currentTarget.style.setProperty('--y', `${y}px`);
                }}
              >
                {/* Hover glow */}
                <div
                  className="absolute -inset-px opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl pointer-events-none"
                  style={{
                    background:
                      s.accent === "purple"
                        ? "radial-gradient(400px circle at var(--x,50%) var(--y,50%), rgba(108,99,255,0.12), transparent 60%)"
                        : s.accent === "cyan"
                        ? "radial-gradient(400px circle at var(--x,50%) var(--y,50%), rgba(0,229,255,0.10), transparent 60%)"
                        : "radial-gradient(400px circle at var(--x,50%) var(--y,50%), rgba(255,255,255,0.04), transparent 60%)",
                  }}
                />

                <div className="relative flex items-start justify-between mb-8">
                  <div
                    className={`w-11 h-11 rounded-xl border flex items-center justify-center ${
                      s.accent === "purple"
                        ? "border-accent-purple/30 bg-accent-purple/5 text-accent-purple"
                        : s.accent === "cyan"
                        ? "border-accent-cyan/30 bg-accent-cyan/5 text-accent-cyan"
                        : "border-white/10 bg-white/5 text-white"
                    }`}
                  >
                    <Icon size={18} strokeWidth={1.8} />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
                    {s.tag}
                  </span>
                </div>

                <div className="relative flex-1">
                  <h3 className="font-display text-xl md:text-2xl tracking-tight mb-3">
                    {s.title}
                  </h3>
                  <p className="text-sm md:text-base text-zinc-400 leading-relaxed max-w-md">
                    {s.desc}
                  </p>

                  {/* Feature bullets */}
                  <ul className="mt-5 grid grid-cols-1 xl:grid-cols-2 gap-x-4 gap-y-2">
                    {s.features.map((f) => (
                      <li key={f} className="flex items-center gap-2">
                        <Check
                          size={11}
                          strokeWidth={2.5}
                          className={
                            s.accent === "purple"
                              ? "text-accent-purple shrink-0"
                              : s.accent === "cyan"
                              ? "text-accent-cyan shrink-0"
                              : "text-zinc-400 shrink-0"
                          }
                        />
                        <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-zinc-500 group-hover:text-zinc-300 transition-colors">
                          {f}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
